import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";
import { PUBLIC_PAGES, SERVICE_PAGES, SITE_URL, hasCatalogServices } from "./pages";

// Update the existing index.html tags rather than creating a competing head system.
function setHeadTag(selector, tagName, attributes) {
  const matches = [...document.head.querySelectorAll(selector)];
  const element = matches.shift() || document.createElement(tagName);
  matches.forEach(duplicate => duplicate.remove());
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  if (!element.parentNode) document.head.appendChild(element);
  return element;
}

export default function PageMetadata() {
  const { pathname } = useLocation();
  const { categories, loading, error, CONTACT } = useCatalog();
  const path = pathname.replace(/\/+$/, "") || "/";
  const servicePage = SERVICE_PAGES.find(page => page.path === path);
  const category = categories.find(item => item.id === servicePage?.categoryId);
  const metadata = PUBLIC_PAGES[path] || servicePage;
  const unavailable = servicePage && !loading && !error && !hasCatalogServices(category);

  useEffect(() => {
    document.title = unavailable ? "Service currently unavailable | Repairro" : metadata?.title || "Repairro";
    setHeadTag('meta[name="description"]', "meta", {
      name: "description",
      content: unavailable ? "Contact Repairro for current service availability in Lucknow." : metadata?.description || "Repairro customer and service application.",
    });
    // No private booking identifiers or query parameters belong in canonical URLs.
    if (metadata) {
      setHeadTag('link[rel="canonical"]', "link", { rel: "canonical", href: `${SITE_URL}${path === "/" ? "/" : path}` });
    } else {
      document.head.querySelectorAll('link[rel="canonical"]').forEach(tag => tag.remove());
    }
    setHeadTag('meta[name="robots"]', "meta", { name: "robots", content: metadata && !unavailable ? "index, follow" : "noindex, follow" });

    const organization = {
      "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "Repairro",
      url: `${SITE_URL}/`, telephone: CONTACT.phone, email: CONTACT.email,
    };
    let structuredData;
    if (path === "/") {
      structuredData = { "@context": "https://schema.org", ...organization };
    } else if (servicePage && !loading && !error && hasCatalogServices(category) && category.status !== "soon") {
      structuredData = {
        "@context": "https://schema.org", "@type": "Service",
        name: servicePage.h1, serviceType: category.name, url: `${SITE_URL}${path}`,
        areaServed: { "@type": "City", name: "Lucknow" }, provider: organization,
      };
    }
    document.getElementById("repairro-structured-data")?.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.id = "repairro-structured-data";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [path, metadata, servicePage, unavailable, category, loading, error, CONTACT]);
  return null;
}
